// Report It API client (v7 §6.1, MV-1077). Submits a user incident report —
// rough symptom text + correlation ids + the captured client snapshot — to the
// observability-analysis Lambda's /reports endpoint.
//
// Uses the shared $api instance so every call carries the JWT (auth) and the
// X-Mytaverse-Correlation-Id header (the cross-system id the backend stamps on
// its logs), exactly as the rest of the observability integration does.

import $api from '../../http/axios';
import { AttachmentPartDeclaration } from './attachmentParts';
import { ReportItClientSnapshot } from './snapshotTypes';

// Relative to the $api baseURL (env.REACT_APP_API_URL). Mirrors the backend's
// getObservabilityReportsApiBasePath() = '/api/v2/observability/reports'.
const REPORTS_PATH = 'api/v2/observability/reports';

export type ReportIncidentType =
  | 'black_screen'
  | 'audio_only'
  | 'frozen_video'
  | 'disconnect'
  | 'other';

export type ReportSymptomHint =
  | 'screen_black'
  | 'audio_still_playing'
  | 'video_frozen'
  | 'error_popup'
  | 'controls_not_working'
  | 'disconnected'
  | 'other';

export type ReportSeverityHint = 'low' | 'medium' | 'high';

/** Non-identifying correlation ids the client may attach (v7 §13 — identity is
 *  NOT sent from the client; the backend derives it from auth). */
export interface ReportCorrelationEntities {
  browserSessionId?: string;
  sentryTraceId?: string;
  websocketSessionId?: string;
  streamSessionId?: string;
  streamGroupId?: string;
  /** AWS region of the GameLift Streams session. */
  region?: string;
  placementId?: string;
  instanceId?: string;
  requestId?: string;
}

/** How the user added an attachment — surfaced to the backend manifest (v7). */
export type ReportAttachmentSource = 'manual_upload' | 'paste' | 'drag_drop';

/** The attachment classes the MVP accepts (§8.1). */
export type ReportAttachmentKind =
  | 'screenshot'
  | 'screen_recording'
  | 'log_file';

export interface ReportAttachmentDeclaration {
  filename?: string;
  contentType: string;
  byteSize: number;
  source?: ReportAttachmentSource;
  /** The exact split the backend will mint part URLs for (§8.2). */
  parts: AttachmentPartDeclaration[];
}

export interface ReportAttachmentFile {
  file: File;
  filename?: string;
  contentType: string;
  byteSize: number;
  source?: ReportAttachmentSource;
  kind: ReportAttachmentKind;
  parts: AttachmentPartDeclaration[];
}

export interface ReportAttachmentKindPolicy {
  contentTypes: string[];
  extensions: string[];
  maxFiles: number;
  maxBytes: number;
}

// The limit fields are optional so a hub build newer than the deployed backend
// degrades to an explicit "attachments unavailable" state instead of rendering
// its own guesses at the caps the backend will enforce.
export interface ReportAttachmentPolicy {
  attachmentsEnabled: boolean;
  inAppCaptureEnabled: boolean;
  ocrEnabled: boolean;
  visualClassificationEnabled: boolean;
  tenantPolicyVersion: string;
  approvalId?: string;
  approvedProcessorRegion?: string;
  allowedAttachmentTypes?: string[];
  maxAttachments?: number;
  byKind?: Partial<Record<ReportAttachmentKind, ReportAttachmentKindPolicy>>;
  maxTotalBytes?: number;
  multipartPartBytes?: number;
  retentionDays?: number;
  uploadPartUrlTtlSeconds?: number;
  retryWindowSeconds?: number;
}

/** The policy fields the dialog needs before it can accept a single file. */
export interface ResolvedAttachmentLimits {
  allowedAttachmentTypes: string[];
  maxAttachments: number;
  byKind: Record<ReportAttachmentKind, ReportAttachmentKindPolicy>;
  maxTotalBytes: number;
  multipartPartBytes: number;
  retryWindowSeconds: number;
}

const REQUIRED_POLICY_KINDS: ReportAttachmentKind[] = [
  'screenshot',
  'screen_recording',
  'log_file',
];

/**
 * Narrow a policy to the limits the UI renders from, or undefined when the
 * backend did not state them — §8.1 makes /reports/policy the single source of
 * truth, so the client must not substitute constants of its own.
 */
export const resolveAttachmentLimits = (
  policy: ReportAttachmentPolicy | undefined,
): ResolvedAttachmentLimits | undefined => {
  if (!policy) {
    return undefined;
  }

  const {
    allowedAttachmentTypes,
    maxAttachments,
    byKind,
    maxTotalBytes,
    multipartPartBytes,
    retryWindowSeconds,
  } = policy;

  if (
    !allowedAttachmentTypes?.length ||
    !maxAttachments ||
    !maxTotalBytes ||
    !multipartPartBytes ||
    !byKind ||
    REQUIRED_POLICY_KINDS.some((kind) => !byKind[kind])
  ) {
    return undefined;
  }

  return {
    allowedAttachmentTypes,
    maxAttachments,
    byKind: byKind as Record<ReportAttachmentKind, ReportAttachmentKindPolicy>,
    maxTotalBytes,
    multipartPartBytes,
    retryWindowSeconds: retryWindowSeconds ?? 0,
  };
};

/**
 * The kind a file will be checked against. Derived from the browser MIME type
 * only, mirroring the backend — a client-chosen kind would let the caller pick
 * which per-kind cap applies to it.
 */
export const kindForContentType = (
  contentType: string,
  limits: ResolvedAttachmentLimits,
): ReportAttachmentKind | undefined =>
  REQUIRED_POLICY_KINDS.find((kind) =>
    limits.byKind[kind].contentTypes.includes(contentType),
  );

export interface SubmitReportInput {
  environment: string;
  eventId?: string;
  /** Idempotency inputs (v7 §10.3) — combined server-side with the auth tenant. */
  browserSessionId: string;
  clientReportNonce: string;

  incidentType: ReportIncidentType;
  occurredAt: string;
  userReportedAt?: string;
  description?: string;
  symptomHints?: ReportSymptomHint[];
  severityHint?: ReportSeverityHint;
  userLocalTime?: string;
  userTimeZone?: string;
  language?: string;
  attachDiagnostics?: boolean;

  entities?: ReportCorrelationEntities;
  attachments?: ReportAttachmentDeclaration[];
  clientSnapshot?: ReportItClientSnapshot;
  /** Web Hub build version (MV-1396). */
  hubVersion?: string;
  /** Spatial Manager service version the client saw (MV-1396). */
  spatialManagerVersion?: string;
  /** Scrubbed, size-capped tail of the browser console log (MV-1396). */
  browserLogs?: string;
}

export interface PresignedUploadPart {
  partNumber: number;
  byteSize: number;
  uploadUrl: string;
  /** Echoed by the backend so we send the header its signature covers. */
  checksumSha256Base64: string;
}

export interface PresignedAttachmentUpload {
  attachmentId: string;
  uploadAttemptId: string;
  expiresInSeconds: number;
  parts: PresignedUploadPart[];
}

export type AttachmentUploadStatus =
  | 'awaiting_upload'
  | 'completing'
  | 'uploaded'
  | 'expired'
  | 'cancelled';

export type AttachmentAvailabilityStatus =
  | 'pending'
  | 'approved'
  | 'blocked'
  | 'failed'
  | 'expired';

export interface ReportAttachmentState {
  attachmentId: string;
  filename?: string;
  contentType: string;
  byteSize: number;
  uploadStatus: AttachmentUploadStatus;
  availabilityStatus: AttachmentAvailabilityStatus;
  uploadAttemptExpiresAt?: string;
}

export interface SubmitReportResponse {
  reportId: string;
  status: string;
  created: boolean;
  createdAt: string;
  /** Only present on a first create — a duplicate POST returns no part URLs. */
  attachmentUploads?: PresignedAttachmentUpload[];
  attachments?: ReportAttachmentState[];
}

export interface ReportPolicyResponse {
  tenantId: string;
  environment: string;
  attachmentPolicy: ReportAttachmentPolicy;
}

/**
 * Build the POST /reports body (pure). Only present, non-empty fields are
 * included — the backend validator treats most as optional and rejects unknown
 * shapes, so we keep the payload tight.
 */
export const buildReportBody = (
  input: SubmitReportInput,
): Record<string, unknown> => {
  const body: Record<string, unknown> = {
    environment: input.environment,
    browserSessionId: input.browserSessionId,
    clientReportNonce: input.clientReportNonce,
    incidentType: input.incidentType,
    occurredAt: input.occurredAt,
  };

  if (input.eventId) {
    body.eventId = input.eventId;
  }

  if (input.userReportedAt) {
    body.userReportedAt = input.userReportedAt;
  }

  if (input.description) {
    body.description = input.description;
  }

  if (input.symptomHints && input.symptomHints.length > 0) {
    body.symptomHints = input.symptomHints;
  }

  if (input.severityHint) {
    body.severityHint = input.severityHint;
  }

  if (input.userLocalTime) {
    body.userLocalTime = input.userLocalTime;
  }

  if (input.userTimeZone) {
    body.userTimeZone = input.userTimeZone;
  }

  if (input.language) {
    body.language = input.language;
  }

  if (input.attachDiagnostics) {
    body.attachDiagnostics = true;
  }

  if (input.entities && Object.keys(input.entities).length > 0) {
    body.entities = input.entities;
  }

  if (input.attachments && input.attachments.length > 0) {
    body.attachments = input.attachments;
  }

  if (input.clientSnapshot) {
    body.clientSnapshot = input.clientSnapshot;
  }

  if (input.hubVersion) {
    body.hubVersion = input.hubVersion;
  }

  if (input.spatialManagerVersion) {
    body.spatialManagerVersion = input.spatialManagerVersion;
  }

  if (input.browserLogs) {
    body.browserLogs = input.browserLogs;
  }

  return body;
};

/** Submit a report. Returns the created (or idempotently-resolved) report. */
export const submitReport = async (
  input: SubmitReportInput,
): Promise<SubmitReportResponse> => {
  const { data } = await $api.post<SubmitReportResponse>(
    REPORTS_PATH,
    buildReportBody(input),
  );

  return data;
};

export const getReportPolicy = async (): Promise<ReportPolicyResponse> => {
  const { data } = await $api.get<ReportPolicyResponse>(`${REPORTS_PATH}/policy`);
  return data;
};

export const buildAttachmentDeclarations = (
  files: ReportAttachmentFile[] | undefined,
): ReportAttachmentDeclaration[] | undefined => {
  if (!files || files.length === 0) {
    return undefined;
  }

  return files.map((attachment) => ({
    filename: attachment.filename || attachment.file.name || undefined,
    contentType: attachment.contentType,
    byteSize: attachment.byteSize,
    source: attachment.source,
    parts: attachment.parts,
  }));
};

export interface AttachmentPartPutArgs {
  url: string;
  body: Blob;
  headers: Record<string, string>;
  /** Reports this part's upload progress as a 0..1 fraction. */
  onProgress?: (fraction: number) => void;
}

/** Resolves with the part's S3 ETag, which completion must quote back. */
export type AttachmentPartPut = (args: AttachmentPartPutArgs) => Promise<string>;

const stripQuotes = (etag: string): string => etag.replace(/^"|"$/g, '');

// Bytes sent are not bytes stored: S3 rejects a part only once the whole body
// has been transmitted, so a doomed upload still reaches 100% of its transfer.
// In-flight progress therefore stops short and completion is reserved for the
// accepted response, or the bar reads "done" for a part that then fails.
const IN_FLIGHT_PROGRESS_CEILING = 0.99;

// Default PUT via XMLHttpRequest — unlike fetch, XHR exposes real upload
// progress events, which drives the per-file progress bars. Resolves on a 2xx.
const xhrPartPut: AttachmentPartPut = ({ url, body, headers, onProgress }) =>
  new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);

    Object.entries(headers).forEach(([name, value]) => {
      xhr.setRequestHeader(name, value);
    });

    if (xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (onProgress && event.lengthComputable && event.total > 0) {
          onProgress(
            Math.min(event.loaded / event.total, 1) * IN_FLIGHT_PROGRESS_CEILING,
          );
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`Attachment part upload failed (${xhr.status})`));
        return;
      }

      const etag = xhr.getResponseHeader('ETag');

      if (!etag) {
        reject(new Error('Attachment part upload returned no ETag'));
        return;
      }

      onProgress?.(1);
      resolve(stripQuotes(etag));
    };
    xhr.onerror = () => reject(new Error('Attachment part network error'));
    xhr.onabort = () => reject(new Error('Attachment part upload aborted'));

    xhr.send(body);
  });

export interface CompletedPartClaim {
  partNumber: number;
  etag: string;
  checksumSha256Base64: string;
}

export interface UploadAttachmentPartsOptions {
  /** Whole-file progress (0..1) across the part set. */
  onProgress?: (fraction: number) => void;
  /** Injectable PUT for tests; defaults to an XHR upload with progress. */
  putPart?: AttachmentPartPut;
}

/**
 * Upload one attachment's fixed part set, in order, and collect the claims the
 * backend needs to complete it. Each part carries the checksum header the
 * signature was computed over, so a corrupted slice is refused by S3 rather
 * than silently stored.
 */
export const uploadAttachmentParts = async (
  file: Blob,
  upload: PresignedAttachmentUpload,
  options: UploadAttachmentPartsOptions = {},
): Promise<CompletedPartClaim[]> => {
  const putPart = options.putPart ?? xhrPartPut;
  const totalBytes = upload.parts.reduce((sum, part) => sum + part.byteSize, 0);
  const claims: CompletedPartClaim[] = [];

  let uploadedBytes = 0;
  let offset = 0;

  for (let i = 0; i < upload.parts.length; i += 1) {
    const part = upload.parts[i];
    const body = file.slice(offset, offset + part.byteSize);
    const partStart = uploadedBytes;

    // Sequential: S3 rejects a part set the backend did not sign, so there is
    // nothing to gain from racing parts against a fixed plan, and a phone on a
    // weak uplink does better with one stream than five.
    // eslint-disable-next-line no-await-in-loop
    const etag = await putPart({
      url: part.uploadUrl,
      body,
      headers: {
        'x-amz-checksum-sha256': part.checksumSha256Base64,
      },
      onProgress: options.onProgress
        ? (fraction) => {
            const done = partStart + fraction * part.byteSize;
            options.onProgress?.(totalBytes > 0 ? done / totalBytes : 1);
          }
        : undefined,
    });

    claims.push({
      partNumber: part.partNumber,
      etag,
      checksumSha256Base64: part.checksumSha256Base64,
    });

    uploadedBytes += part.byteSize;
    offset += part.byteSize;
  }

  return claims;
};

const attachmentPath = (reportId: string, attachmentId: string): string =>
  `${REPORTS_PATH}/${encodeURIComponent(reportId)}/attachments/${encodeURIComponent(
    attachmentId,
  )}`;

/**
 * Hand the uploaded part set to the backend, which is the only party that may
 * call CompleteMultipartUpload — the browser never gets that permission.
 */
export const completeAttachmentUpload = async (
  reportId: string,
  attachmentId: string,
  claim: { uploadAttemptId: string; parts: CompletedPartClaim[] },
): Promise<ReportAttachmentState> => {
  const { data } = await $api.post<ReportAttachmentState>(
    `${attachmentPath(reportId, attachmentId)}/complete`,
    claim,
  );

  return data;
};

/**
 * Ask for a fresh upload attempt for an attachment whose bytes never landed
 * (§8.2). Owner-scoped server-side, and only while the report is still open for
 * evidence, so a failed upload does not cost the user the whole report.
 */
export const retryAttachmentUpload = async (
  reportId: string,
  attachmentId: string,
): Promise<PresignedAttachmentUpload> => {
  const { data } = await $api.post<{
    attachmentUploads: PresignedAttachmentUpload[];
  }>(`${attachmentPath(reportId, attachmentId)}/retry`, {});

  const [upload] = data.attachmentUploads ?? [];

  if (!upload) {
    throw new Error(`Retry returned no upload target: ${attachmentId}`);
  }

  return upload;
};

/**
 * Ask the backend to (re)run analysis for a stored report — used after the
 * client snapshot has been uploaded, or to refresh a verdict (v7 §6.3).
 */
export const requestReportAnalysis = async (
  reportId: string,
): Promise<unknown> => {
  const { data } = await $api.post(
    `${REPORTS_PATH}/${encodeURIComponent(reportId)}/analyse`,
    {},
  );

  return data;
};

/** Best-effort local context for the report (impure — reads browser globals). */
export const collectBrowserReportContext = (): {
  userLocalTime: string;
  userTimeZone?: string;
  language?: string;
} => {
  const context: {
    userLocalTime: string;
    userTimeZone?: string;
    language?: string;
  } = { userLocalTime: new Date().toString() };

  try {
    context.userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // Intl unavailable — omit the time zone.
  }

  if (typeof navigator !== 'undefined' && navigator.language) {
    context.language = navigator.language;
  }

  return context;
};
